import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { HERO_SCENES, type HeroScene } from "./content";
import { useCountUp, useIsActiveOnScreen, usePrefersReducedMotion } from "./hooks";

/** Fases de uma cena: 0 chegou, 1 bot leu, 2 link trocado, 3 resultado. */
type Phase = 0 | 1 | 2 | 3;
const FINAL_PHASE: Phase = 3;
/** Quanto cada fase fica na tela antes da proxima (ms). A ultima segura a cena. */
const PHASE_HOLD_MS: Readonly<Record<Phase, number>> = { 0: 1100, 1: 1000, 2: 900, 3: 4200 };

/**
 * Avanca as fases da cena. Com `autoAdvance`, ao fim passa para a proxima loja;
 * sem ele (o usuario escolheu uma loja), para na fase final.
 */
function useSceneLoop(
  playing: boolean,
  autoAdvance: boolean,
): { scene: number; phase: Phase; select: (index: number) => void } {
  const [scene, setScene] = useState(0);
  const [phase, setPhase] = useState<Phase>(0);

  useEffect(() => {
    if (!playing || (phase === FINAL_PHASE && !autoAdvance)) return;
    const timer = window.setTimeout(() => {
      if (phase < FINAL_PHASE) {
        setPhase((phase + 1) as Phase);
        return;
      }
      setScene((current) => (current + 1) % HERO_SCENES.length);
      setPhase(0);
    }, PHASE_HOLD_MS[phase]);
    return () => window.clearTimeout(timer);
  }, [playing, autoAdvance, phase, scene]);

  const select = (index: number): void => {
    setScene(index);
    setPhase(0);
  };

  return { scene, phase, select };
}

export function HeroDemo(): JSX.Element {
  const reduced = usePrefersReducedMotion();
  const { ref, active } = useIsActiveOnScreen<HTMLDivElement>();
  const [autoAdvance, setAutoAdvance] = useState(true);
  const { scene, phase, select } = useSceneLoop(active && !reduced, autoAdvance);

  const onSelect = (index: number): void => {
    setAutoAdvance(false);
    select(index);
  };

  return (
    <div
      ref={ref}
      className="relative rounded-2xl border border-border-strong bg-surface/80 p-4 lg:min-h-[34.25rem] shadow-[0_30px_80px_-30px_rgb(0_0_0/0.8)] sm:p-6"
    >
      <StoreTabs active={scene} onSelect={onSelect} />
      <SceneView scene={HERO_SCENES[scene] as HeroScene} phase={reduced ? FINAL_PHASE : phase} reduced={reduced} />
    </div>
  );
}

function StoreTabs({ active, onSelect }: { active: number; onSelect: (index: number) => void }): JSX.Element {
  return (
    <div className="mb-5 flex items-center justify-between gap-3">
      <div role="tablist" aria-label="Lojas do exemplo" className="flex gap-1 rounded-lg bg-background/70 p-1">
        {HERO_SCENES.map((item, index) => (
          <button
            key={item.store}
            type="button"
            role="tab"
            aria-selected={index === active}
            onClick={() => onSelect(index)}
            className={cn(
              "lp-press rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors sm:px-3",
              index === active
                ? "bg-surface-overlay text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {item.store}
          </button>
        ))}
      </div>
      <span className="text-xs text-muted-foreground">Exemplo</span>
    </div>
  );
}

interface SceneViewProps {
  scene: HeroScene;
  phase: Phase;
  reduced: boolean;
}

/** Tag curta no fim do link: quebra a linha antes dela, nunca no meio. */
function keepWhole(scene: HeroScene): boolean {
  return scene.linkHead !== "";
}

function SceneView({ scene, phase, reduced }: SceneViewProps): JSX.Element {
  return (
    <div role="tabpanel" aria-label={`Exemplo com ${scene.store}`}>
      <Bubble label="Chegou no grupo de origem" scene={scene}>
        <span className="text-muted-foreground">{scene.linkHead}</span>
        <span className={cn("lp-foreign", keepWhole(scene) && "whitespace-nowrap")} data-on={phase >= 1}>
          {scene.foreign}
        </span>
      </Bubble>

      <Connector phase={phase} />

      <div className="lp-rise" data-on={phase >= 2}>
        <Bubble label="Publicado no seu grupo" scene={scene} own>
          <span className="text-foreground/80">{scene.linkHead}</span>
          <mark
            className={cn("lp-marker bg-transparent text-foreground", keepWhole(scene) && "whitespace-nowrap")}
            data-on={phase >= 2}
          >
            {scene.own}
          </mark>
        </Bubble>
      </div>

      <Result scene={scene} show={phase >= FINAL_PHASE} reduced={reduced} />
    </div>
  );
}

interface BubbleProps {
  label: string;
  scene: HeroScene;
  own?: boolean;
  children: React.ReactNode;
}

function Bubble({ label, scene, own = false, children }: BubbleProps): JSX.Element {
  return (
    <figure className="m-0">
      <figcaption className="mb-1.5 text-xs text-muted-foreground">{label}</figcaption>
      <div
        className={cn(
          "rounded-xl rounded-tl-sm border px-4 py-3",
          own ? "border-signal/35 bg-signal-dim/40" : "border-border bg-background/60",
        )}
      >
        <p className="text-sm font-medium leading-snug text-foreground">{scene.product}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">{scene.price}</p>
        <p className="mt-2 break-all font-mono text-[0.78rem] leading-relaxed sm:text-[0.8125rem]">
          <span className="text-muted-foreground">https://</span>
          {children}
        </p>
      </div>
    </figure>
  );
}

function Connector({ phase }: { phase: Phase }): JSX.Element {
  return (
    <div className="relative flex h-14 items-center gap-3 pl-5">
      <span className="relative h-full w-px overflow-visible bg-border-strong" aria-hidden="true">
        {phase === 1 && <span className="lp-travel absolute -left-[3px] top-0 h-[7px] w-[7px] rounded-full bg-signal" />}
      </span>
      <span className="lp-rise flex items-center gap-1.5 text-xs text-foreground" data-on={phase >= 2}>
        <Check size={14} className="text-signal" aria-hidden="true" />
        LucraLink trocou o link
      </span>
    </div>
  );
}

function Result({ scene, show, reduced }: { scene: HeroScene; show: boolean; reduced: boolean }): JSX.Element {
  const counterRef = useCountUp(scene.commissionCents ?? 0, show && scene.commissionCents !== null, reduced);

  return (
    <div className="lp-rise mt-5 border-t border-border pt-4" data-on={show}>
      {scene.commissionCents !== null ? (
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="text-xs text-muted-foreground">Comissão desta venda (simulação)</p>
            <span
              ref={counterRef}
              className="mt-1 block text-3xl font-semibold tracking-[-0.03em] text-signal tabular-nums"
            />
          </div>
          <p className="max-w-[16rem] text-right text-xs leading-relaxed text-muted-foreground">{scene.whereToTrack}</p>
        </div>
      ) : (
        <p className="text-sm leading-relaxed text-foreground">{scene.whereToTrack}</p>
      )}
      <p className="mt-2 text-xs text-muted-foreground">{scene.how}</p>
    </div>
  );
}
